import {computed, ref} from 'vue';
import {format} from 'date-fns';
import {defineStore} from 'pinia';
import {useTranslation} from '#imports';
import {readonlyState, useErrorHandler} from '@malt/utils';
import type {ActivityReportForbiddenError, DetailedActivityReportResource, WorkAmountResource, PaymentMethod} from '@api';
import {ActivityReportStatusResource, FetchError, ReportTypeResource} from '@api';
import {activityTrackerEndpoints} from '@/registry';

export const useActivityTrackerStore = defineStore('activity-tracker', () => {
    const projectIdString = ref<string>('');
    const month = ref<number>(0);
    const year = ref<number>(0);
    const estimatedWorkAmount = ref<number>(0);
    const unit = ref<string>();
    const reportType = ref<ReportTypeResource>();
    const clientPaymentMethod = ref<PaymentMethod>();
    const status = ref<ActivityReportStatusResource>();
    const simpleReportTotalWorkAmount = ref<number | undefined>();
    const detailedReportWorkAmounts = ref<{[key: string]: WorkAmountResource}>();
    const workDescription = ref<string | undefined>();
    const lastSubmissionDate = ref<string | undefined>();
    const projectSubject = ref<string>();
    const isProjectLotFinal = ref<boolean>();
    const activityReportId = ref<string>();
    const shouldDisplayUmbrellaCompanyAffiliationDialog = ref<boolean>();
    const shouldDisplayBusinessDetailsDialog = ref(false);
    const {t} = useTranslation();
    const {handleFetchError} = useErrorHandler();

    async function fetchActivityTracker() {
        try {
            const data = await activityTrackerEndpoints.getActivityReportById({
                activityReportId: activityReportId.value!,
            });

            projectIdString.value = data.projectId;
            month.value = data.month;
            year.value = data.year;
            unit.value = data.unit;
            estimatedWorkAmount.value = data.estimatedWorkAmount;
            clientPaymentMethod.value = data.paymentMethod;
            reportType.value = data.reportType;
            status.value = data.status;
            simpleReportTotalWorkAmount.value =
                status.value === ActivityReportStatusResource.Empty && data.totalWorkAmount === 0 ? undefined : data.totalWorkAmount;
            lastSubmissionDate.value = data.lastFreelancerSubmissionDate && format(new Date(data.lastFreelancerSubmissionDate), 'MMM dd, yyyy');
            projectSubject.value = data.projectSubject;
            isProjectLotFinal.value = data.projectLotIsTheFinalLot;
            workDescription.value = data.workDescription;

            if (data.reportType === ReportTypeResource.Detailed) {
                const detailedData = data as DetailedActivityReportResource;
                detailedReportWorkAmounts.value = detailedData.workAmounts;
            }
        } catch (e) {
            handleFetchError(e as FetchError, {customErrorMessage: t('generic.error.unexpected')});
            return Promise.reject(e);
        }
    }

    async function saveAndQuitDetailedReport(workAmountsByDays?: {[key: string]: WorkAmountResource}) {
        try {
            await activityTrackerEndpoints.fillDraftActivityReport({
                activityReportId: activityReportId.value!,
                fillActivityReportRequest: {workAmountsByDays, workDescription: workDescription.value},
            });
        } catch (e) {
            handleFetchError(e as FetchError, {customErrorMessage: t('generic.error.unexpected')});
            return Promise.reject(e);
        }
    }

    async function saveAndQuitSimpleReport(totalWorkAmount: number) {
        try {
            await activityTrackerEndpoints.fillDraftActivityReport({
                activityReportId: activityReportId.value!,
                fillActivityReportRequest: {totalWorkAmount, workDescription: workDescription.value},
            });
        } catch (e) {
            handleFetchError(e as FetchError, {customErrorMessage: t('generic.error.unexpected')});
            return Promise.reject(e);
        }
    }

    function handleForbiddenSubmitError(e: FetchError) {
        const body = e.getCauseData() as ActivityReportForbiddenError | undefined;
        if (body?.reason === 'BUSINESS_DETAILS_INVALID') {
            shouldDisplayBusinessDetailsDialog.value = true;
        } else if (body?.reason === 'ONGOING_UMBRELLA_COMPANY_AFFILIATION') {
            shouldDisplayUmbrellaCompanyAffiliationDialog.value = true;
        } else {
            handleFetchError(e, {customErrorMessage: t('generic.error.unexpected')});
        }
    }

    async function submitActivityTrackerSimpleReport(totalWorkAmount: number) {
        try {
            await activityTrackerEndpoints.sendActivityReportToClient({
                activityReportId: activityReportId.value!,
                fillActivityReportRequest: {totalWorkAmount, workDescription: workDescription.value},
            });
        } catch (e) {
            if (e instanceof FetchError && e.statusCode === 403) {
                handleForbiddenSubmitError(e);
            } else {
                handleFetchError(e as FetchError, {customErrorMessage: t('generic.error.unexpected')});
            }
            return Promise.reject(e);
        }
    }

    async function submitActivityTrackerSimpleReportByAdmin(totalWorkAmount: number) {
        try {
            await activityTrackerEndpoints.sendActivityReportToClientByAdmin({
                activityReportId: activityReportId.value!,
                fillActivityReportRequest: {totalWorkAmount, workDescription: workDescription.value},
            });
        } catch (e) {
            if (e instanceof FetchError && e.statusCode === 403) {
                handleForbiddenSubmitError(e);
            } else {
                handleFetchError(e as FetchError, {customErrorMessage: t('generic.error.unexpected')});
            }
            return Promise.reject(e);
        }
    }

    async function submitActivityTrackerDetailedReport(workAmountsByDays?: {[key: string]: WorkAmountResource}) {
        try {
            await activityTrackerEndpoints.sendActivityReportToClient({
                activityReportId: activityReportId.value!,
                fillActivityReportRequest: {workAmountsByDays, workDescription: workDescription.value},
            });
        } catch (e) {
            if (e instanceof FetchError && e.statusCode === 403) {
                handleForbiddenSubmitError(e);
            } else {
                handleFetchError(e as FetchError, {customErrorMessage: t('generic.error.unexpected')});
            }
            return Promise.reject(e);
        }
    }

    async function submitActivityTrackerDetailedReportByAdmin(workAmountsByDays?: {[key: string]: WorkAmountResource}) {
        try {
            await activityTrackerEndpoints.sendActivityReportToClientByAdmin({
                activityReportId: activityReportId.value!,
                fillActivityReportRequest: {workAmountsByDays, workDescription: workDescription.value},
            });
        } catch (e) {
            if (e instanceof FetchError && e.statusCode === 403) {
                handleForbiddenSubmitError(e);
            } else {
                handleFetchError(e as FetchError, {customErrorMessage: t('generic.error.unexpected')});
            }
            return Promise.reject(e);
        }
    }

    async function fetchActivityReportIdByProjectLotId(projectId: string, projectLotId: string) {
        try {
            const timesheet = await activityTrackerEndpoints.getActivityReportByProjectLotId({
                projectId,
                projectLotId,
            });
            return timesheet.activityReportId;
        } catch (e) {
            handleFetchError(e as FetchError, {customErrorMessage: t('generic.error.unexpected')});
            return Promise.reject(e);
        }
    }

    function setActivityReportId(newActivityReportId: string) {
        activityReportId.value = newActivityReportId;
    }

    const isProjectCancelled = computed(() => status.value === ActivityReportStatusResource.Cancelled);
    const isReportValidated = computed(() => status.value === ActivityReportStatusResource.ValidatedByClient);

    const isReportTypeDetailed = computed(() => reportType.value === ReportTypeResource.Detailed);

    const isReportSubmittedToClient = computed(() => status.value === ActivityReportStatusResource.SubmittedToClient);

    const detailedReportWorkTotalAmount = computed(
        () => detailedReportWorkAmounts.value && Object.values(detailedReportWorkAmounts.value).reduce((acc, {amount}) => acc + amount, 0),
    );

    function setDetailedReportWorkAmounts(value: {[key: string]: WorkAmountResource}) {
        detailedReportWorkAmounts.value = value;
    }

    function setSimpleReportTotalWorkAmount(value: number | undefined) {
        simpleReportTotalWorkAmount.value = value;
    }

    function setShouldDisplayUmbrellaCompanyAffiliationDialog(newShouldDisplayUmbrellaCompanyAffiliationDialog: boolean) {
        shouldDisplayUmbrellaCompanyAffiliationDialog.value = newShouldDisplayUmbrellaCompanyAffiliationDialog;
    }

    function setShouldDisplayBusinessDetailsDialog(value: boolean) {
        shouldDisplayBusinessDetailsDialog.value = value;
    }

    return {
        isProjectCancelled,
        isReportValidated,
        isReportTypeDetailed,
        isReportSubmittedToClient,
        detailedReportWorkTotalAmount,
        year: readonlyState(year),
        month: readonlyState(month),
        unit: readonlyState(unit),
        clientPaymentMethod: readonlyState(clientPaymentMethod),
        estimatedWorkAmount: readonlyState(estimatedWorkAmount),
        projectIdString: readonlyState(projectIdString),
        lastSubmissionDate: readonlyState(lastSubmissionDate),
        projectSubject: readonlyState(projectSubject),
        isProjectLotFinal: readonlyState(isProjectLotFinal),
        simpleReportTotalWorkAmount: readonlyState(simpleReportTotalWorkAmount),
        detailedReportWorkAmounts: readonlyState(detailedReportWorkAmounts),
        workDescription,
        activityReportId: readonlyState(activityReportId),
        shouldDisplayUmbrellaCompanyAffiliationDialog: readonlyState(shouldDisplayUmbrellaCompanyAffiliationDialog),
        shouldDisplayBusinessDetailsDialog: readonlyState(shouldDisplayBusinessDetailsDialog),
        saveAndQuitDetailedReport,
        saveAndQuitSimpleReport,
        submitActivityTrackerSimpleReport,
        submitActivityTrackerSimpleReportByAdmin,
        submitActivityTrackerDetailedReport,
        submitActivityTrackerDetailedReportByAdmin,
        fetchActivityTracker,
        setDetailedReportWorkAmounts,
        setSimpleReportTotalWorkAmount,
        setActivityReportId,
        setShouldDisplayUmbrellaCompanyAffiliationDialog,
        setShouldDisplayBusinessDetailsDialog,
        fetchActivityReportIdByProjectLotId,
    };
});
